import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { SearchQueryOrder, SearchQueryRequestedPage } from "types/generated";

import {
  SpaceDetailsAccessGrantSource,
  SpaceDetailsAccessGrantedVia,
} from "../components/AccessCells/types";
import { USERS_WITH_ACCESS_TO_SPACE, USERS_WITH_ACCESS_TO_SPACE_COUNT } from "./gql";
import { SpaceDetailsUser } from "./types";

type UseSpaceDetailsUsersProps = {
  spaceId: string;
  orderBy: SearchQueryOrder;
  first: number;
  fullTextSearch: string | null;
};

const useSpaceDetailsUsers = ({
  spaceId,
  orderBy,
  first,
  fullTextSearch,
}: UseSpaceDetailsUsersProps) => {
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  const resetKey = `${spaceId}:${orderBy.field}:${orderBy.direction}:${fullTextSearch ?? ""}`;
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);

  if (previousResetKey !== resetKey) {
    setPreviousResetKey(resetKey);
    setRequestedPage(null);
    setAfter(null);
  }

  const variables = useMemo(
    () => ({
      space: spaceId,
      input: {
        predicates: [],
        orderBy: [orderBy],
        fullTextSearch,
        first,
        after,
        requestedPage,
      },
    }),
    [spaceId, orderBy, first, after, requestedPage, fullTextSearch]
  );

  const { error, loading, data, networkStatus, previousData } = usePolledQuery(
    USERS_WITH_ACCESS_TO_SPACE,
    {
      variables,
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const { data: countData, previousData: previousCountData } = usePolledQuery(
    USERS_WITH_ACCESS_TO_SPACE_COUNT,
    {
      variables: { space: spaceId, input: { fields: [], fullTextSearch, predicates: [] } },
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const loadUsers = useCallback(
    ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      setAfter(cursor);
      setRequestedPage(requestedPage);
    },
    []
  );

  const initialLoading =
    loading && networkStatus === NetworkStatus.loading && !data?.usersWithAccessToSpace;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const users = useMemo<SpaceDetailsUser[]>(() => {
    const edges =
      data?.usersWithAccessToSpace?.edges || previousData?.usersWithAccessToSpace?.edges || [];

    return edges.map(({ node }) => ({
      id: node.user.id,
      name: node.user.username,
      roleBindings: node.roles.map((grant) => {
        const grantedVia: SpaceDetailsAccessGrantedVia =
          grant.grantedVia.__typename === "SpaceAccessGroup"
            ? { kind: "group", id: grant.grantedVia.id, name: grant.grantedVia.groupName }
            : {
                kind: "space",
                id: grant.space.id,
                name: grant.space.name,
                source: grant.propagated
                  ? SpaceDetailsAccessGrantSource.PROPAGATED
                  : SpaceDetailsAccessGrantSource.RESIDENT,
              };

        return {
          id: `${grant.role.id}:${grantedVia.kind}:${grantedVia.id}`,
          role: { id: grant.role.id, name: grant.role.name, isSystem: grant.role.isSystem },
          grantedVia,
        };
      }),
    }));
  }, [data?.usersWithAccessToSpace?.edges, previousData?.usersWithAccessToSpace?.edges]);

  const totalCount =
    countData?.usersWithAccessToSpaceSuggestions.filteredCount ??
    previousCountData?.usersWithAccessToSpaceSuggestions.filteredCount ??
    0;

  return {
    loadUsers,
    error,
    users,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor: data?.usersWithAccessToSpace?.pageInfo.endCursor,
    startCursor: data?.usersWithAccessToSpace?.pageInfo.startCursor,
    paginationResetKey: resetKey,
  };
};

export default useSpaceDetailsUsers;
